import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import auditService from "../services/AuditService";
import authService from "../services/AuthService";
import sessionService from "../services/SessionService";
import { t, useLanguage } from "../useTranslations";
import ConfirmModal from "./ConfirmModal";
import "../styles/Login.css";

// Shield SVG icon for branding
const ShieldIcon = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2L3 6v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6L12 2z"
      fill={color}
      opacity="0.9"
    />
    <path
      d="M10.5 15.5l-3-3 1.06-1.06 1.94 1.94 4.44-4.44 1.06 1.06-5.5 5.5z"
      fill="white"
    />
  </svg>
);

const Login = ({ onLogin }) => {
  useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ isOpen: false, message: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await authService.authenticate(email, password);

    if (result.success) {
      await sessionService.createSession(email, result.token);
      auditService.logLogin(true);
      if (onLogin) onLogin();
      navigate('/dashboard');
    } else {
      auditService.logLogin(false);
      setIsLoading(false);
      const errorKey = result.error === 'jwt_not_configured'
        ? 'login.jwtNotConfigured'
        : 'login.error';
      setAlertModal({ isOpen: true, message: t(errorKey) });
    }
  };

  return (
    <div className="login-page">
      <ConfirmModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        variant="warning"
        hideCancel={true}
        onConfirm={() => setAlertModal({ isOpen: false, message: '' })}
      />

      {/* Left branding panel (desktop only) */}
      <div className="login-branding">
        <div className="login-branding-inner">
          <div className="login-logo-mark">
            <div className="login-logo-icon">
              <ShieldIcon size={28} color="white" />
            </div>
            <div className="login-logo-text">
              <span className="login-logo-name">RMS</span>
            </div>
          </div>

          <div className="login-brand-hero">
            <h2 className="login-brand-headline">{t('login.tagline')}</h2>
            <div className="login-brand-features">
              <div className="login-brand-feature">
                <span className="login-brand-feature-dot" />
                <span>{t('login.feature1')}</span>
              </div>
              <div className="login-brand-feature">
                <span className="login-brand-feature-dot" />
                <span>{t('login.feature2')}</span>
              </div>
              <div className="login-brand-feature">
                <span className="login-brand-feature-dot" />
                <span>{t('login.feature3')}</span>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Right form panel */}
      <div className="login-form-panel">
        <div className="login-card">
          {/* Mobile logo */}
          <div className="login-mobile-logo">
            <div className="login-mobile-logo-icon">
              <ShieldIcon size={22} color="white" />
            </div>
            <span className="login-mobile-logo-name">RMS</span>
          </div>

          <div className="login-card-header">
            <h1 className="login-card-title">{t('login.title')}</h1>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            <div className="login-field">
              <label htmlFor="login-email">{t('login.usernameLabel')}</label>
              <input
                id="login-email"
                type="text"
                placeholder={t('login.usernamePlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                disabled={isLoading}
              />
            </div>

            <div className="login-field">
              <label htmlFor="login-password">{t('login.passwordLabel')}</label>
              <input
                id="login-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              className="login-submit-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="login-submit-spinner" />
                  {t('login.loading')}
                </>
              ) : (
                t('login.submit')
              )}
            </button>
          </form>

        </div>

      </div>
    </div>
  );
};

export default Login;
